import React from "react";
import { useApp } from "../context/AppContext";
import { useAuth } from "../context/AuthContext";
import { useFetch, NeedTenant } from "../hooks/useData";
import { GlassCard, Loading, SectionTitle, Badge, fmtNum } from "../components/common";
import { Button } from "../components/ui/button";
import client from "../lib/api";
import { Lightbulb, Loader2, ArrowUpRight, CheckCircle2, Clock } from "lucide-react";
import { toast } from "sonner";

const CAT_COLOR = {
  Automation: "from-cyan-500 to-blue-500", Bottleneck: "from-amber-500 to-orange-500",
  "Rework Reduction": "from-violet-500 to-purple-500", Compliance: "from-red-500 to-rose-500",
  Resource: "from-emerald-500 to-teal-500",
};

function Inner() {
  const { datasetId } = useApp();
  const { can } = useAuth();
  const { data, loading, reload } = useFetch("/recommendations");
  const [gen, setGen] = React.useState(false);

  if (loading) return <Loading />;
  const recs = data || [];

  const generate = async () => {
    if (!datasetId) return toast.error("Select a dataset first");
    setGen(true);
    try { await client.post("/recommendations/generate", { dataset_id: datasetId }); await reload(); toast.success("New recommendations generated"); }
    catch { toast.error("Generation failed"); } finally { setGen(false); }
  };
  const setStatus = async (id, status) => {
    try { await client.patch(`/recommendations/${id}`, { status }); await reload(); } catch { toast.error("Update failed"); }
  };

  return (
    <div className="space-y-5 pm-fade-up">
      <SectionTitle eyebrow="Optimisation" title="Recommendation Engine" sub="Prioritised, ROI-quantified improvement opportunities"
        right={can("analyst") && <Button onClick={generate} disabled={gen} data-testid="generate-recs" className="rounded-xl pm-gradient text-white border-0 h-11">{gen ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Lightbulb className="w-4 h-4 mr-2" />Generate with AI</>}</Button>} />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {recs.map((r) => (
          <GlassCard key={r.id} hover className="p-6" data-testid={`recommendation-${r.id}`}>
            <div className="flex items-start justify-between mb-3">
              <div className={`px-2.5 py-1 rounded-lg text-xs font-semibold text-white bg-gradient-to-r ${CAT_COLOR[r.category] || "from-slate-500 to-slate-600"}`}>{r.category}</div>
              <div className="flex items-center gap-2"><Badge kind="impact" value={r.impact} /><Badge value={r.status} /></div>
            </div>
            <h3 className="font-bold text-slate-900 text-lg">{r.title}</h3>
            <p className="text-sm text-slate-500 mt-1.5 leading-relaxed">{r.description}</p>
            <div className="grid grid-cols-3 gap-3 mt-4">
              <div className="text-center p-2 rounded-xl bg-emerald-50"><div className="text-lg font-bold text-emerald-700 flex items-center justify-center gap-0.5"><ArrowUpRight className="w-4 h-4" />{r.estimated_roi_percent}%</div><div className="text-[10px] text-emerald-600/80 uppercase tracking-wide">ROI</div></div>
              <div className="text-center p-2 rounded-xl bg-cyan-50"><div className="text-lg font-bold text-cyan-700">${fmtNum(r.estimated_savings_usd)}</div><div className="text-[10px] text-cyan-600/80 uppercase tracking-wide">Savings</div></div>
              <div className="text-center p-2 rounded-xl bg-slate-100"><div className="text-lg font-bold text-slate-700">{r.effort}</div><div className="text-[10px] text-slate-500 uppercase tracking-wide">Effort</div></div>
            </div>
            {can("analyst") && (
              <div className="flex gap-2 mt-4">
                <Button onClick={() => setStatus(r.id, "in_progress")} data-testid={`rec-progress-${r.id}`} variant="outline" size="sm" className="rounded-lg flex-1"><Clock className="w-3.5 h-3.5 mr-1" />In progress</Button>
                <Button onClick={() => setStatus(r.id, "done")} data-testid={`rec-done-${r.id}`} size="sm" className="rounded-lg flex-1 bg-emerald-600 hover:bg-emerald-700 text-white border-0"><CheckCircle2 className="w-3.5 h-3.5 mr-1" />Adopt</Button>
              </div>
            )}
          </GlassCard>
        ))}
      </div>
    </div>
  );
}

export default function Recommendations() { return <NeedTenant><Inner /></NeedTenant>; }
